import React, { createContext, useContext, useState } from "react";

/**
 * ModalContext provides global state to track when blocking modals are open.
 * Uses a counter-based approach to handle multiple concurrent modals.
 * This is used to disable background interactions (like the sidebar toggle button)
 * when a modal is displayed.
 */
const ModalContext = createContext({
  isModalOpen: false,
  incrementModalCount: () => {},
  decrementModalCount: () => {},
});

export const ModalProvider = ({ children }) => {
  const [modalCount, setModalCount] = useState(0);

  const incrementModalCount = () => {
    setModalCount((prev) => prev + 1);
  };

  const decrementModalCount = () => {
    setModalCount((prev) => Math.max(0, prev - 1));
  };

  const isModalOpen = modalCount > 0;

  return (
    <ModalContext.Provider
      value={{ isModalOpen, incrementModalCount, decrementModalCount }}
    >
      {children}
    </ModalContext.Provider>
  );
};

/**
 * Hook to access modal state
 * @returns {{ isModalOpen: boolean, incrementModalCount: () => void, decrementModalCount: () => void }}
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
